import { ContactInfoMode } from './contact-info.validator';

/**
 * Mirror of the backend `AllowedContactMethods` enum.
 * Source of truth: `src/Common/AllowedContactMethods.cs` (CPO-4261 — no shared FE export).
 */
const AllowedContactMethods = {
    Email: 1,
    PhoneNumber: 2,
    Both: 3,
} as const;

/**
 * Convert a tenant's `allowedContactMethods` integer to a `ContactInfoMode`.
 *
 * Total over unknown input: anything other than `2` (PhoneNumber) or `3` (Both)
 * — including undefined, null, NaN, out-of-range values, and non-numeric strings
 * — resolves to `'email-only'` (safe default, REQ-SCM-LOGIN-001 AC-4).
 */
export const contactInfoModeFromAllowed = (value: unknown): ContactInfoMode => {
    const n = Number(value);
    if (n === AllowedContactMethods.PhoneNumber) {
        return 'phone-only';
    }
    if (n === AllowedContactMethods.Both) {
        return 'either';
    }
    return 'email-only';
};

/**
 * Canonicalize a contact value before submitting to the auth API.
 *
 * For phone-capable modes (`phone-only` / `either`), strips all non-digit
 * characters and prepends `+1` to produce canonical E.164 for a US/CA number.
 * Uses the same digit-extraction logic as `evaluatePhone` in
 * `contact-info.validator.ts` — strip non-digits, handle leading `1` for
 * 11-digit inputs. Non-phone input (emails, unrecognised) is returned
 * unchanged in `email-only` mode, or when the stripped value doesn't look
 * like a NANP number.
 *
 * REQ-SCM-LOGIN-001 AC-6.
 */
export const normalizeContactForSubmit = (value: string, mode: ContactInfoMode): string => {
    if (mode === 'email-only') {
        return value;
    }

    const digits = value.replace(/\D/g, '');

    // 10-digit national number or 11-digit with leading `1` country code.
    const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

    if (national.length === 10) {
        return `+1${national}`;
    }

    // Not a recognisable NANP number — return as-is (e.g. an email in `either` mode).
    return value;
};
